'use client';

/**
 * ЧАНК ИСЧЕЗ ВМЕСТЕ СО СТАРОЙ СБОРКОЙ — перечитать страницу ОДИН раз.
 *
 * ── Что происходит ───────────────────────────────────────────────────────
 * Имена чанков Next содержат хеш сборки. После выкатки старые файлы с подов
 * пропадают, а открытая вкладка продолжает жить на СТАРОМ HTML и в какой-то
 * момент лениво просит свой чанк — его больше нет. Дальше по обстоятельствам:
 *   • динамический импорт → `ChunkLoadError` внутри рендера → падает
 *     BoardErrorBoundary, человек видит «Доска не открылась»;
 *   • CSS-чанк (`<link>` в `<head>`) → событие `error` на элементе, которое
 *     Next оборачивает в необработанный реджект промиса, и App Router рушит
 *     страницу целиком.
 * В проде это ловилось как issue #30/#31 GlitchTip секунда-в-секунду со сменой
 * подов (разбор 2026-09-09).
 *
 * ── Почему лечение — перезагрузка ────────────────────────────────────────
 * Чинить нечего: файла физически нет. Новый HTML принесёт новые имена чанков,
 * и страница поднимется. Пользователю это стоит одного мигания вместо экрана
 * ошибки.
 *
 * ── Почему с маркером и почему в sessionStorage ──────────────────────────
 * Урок петли перезагрузок гостевой доски (rollback-guard.ts): гард в памяти
 * перезагрузку НЕ переживает, поэтому «одноразовость» нужно хранить снаружи.
 * sessionStorage — ровно нужная область: живёт на вкладку, не тянется в другие
 * вкладки и не переживает закрытие. Если после перезагрузки чанк опять не
 * доехал (сеть легла, прокси кеширует старый HTML), второй раз мы уже НЕ
 * перезагружаемся — показываем честный экран ошибки, а не крутим цикл.
 */

import { markOurReload } from '@/lib/public-board/reload-forensics';

/** Ключ маркера: одна перезагрузка на вкладку за окно. */
const MARK_KEY = '__staleChunkReloadAt';
/** Окно, в котором повторная перезагрузка запрещена. */
export const STALE_CHUNK_RELOAD_WINDOW_MS = 60_000;

/**
 * Ошибка вида «нет такого чанка»?
 *
 * Формулировки у движков разные, и ловить надо все: Chromium даёт
 * `ChunkLoadError` с текстом «Loading chunk … failed», WebKit на том же месте —
 * «Importing a module script failed», Firefox — «error loading dynamically
 * imported module». Проверяем и имя, и текст: имя теряется при пересоздании
 * ошибки, текст — при минификации не теряется.
 */
export function isStaleChunkError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as { name?: unknown }).name;
  if (typeof name === 'string' && name === 'ChunkLoadError') return true;
  const message = (error as { message?: unknown }).message;
  const text = typeof message === 'string' ? message : String(error);
  return (
    /loading chunk \S+ failed/i.test(text) ||
    /loading css chunk/i.test(text) ||
    /importing a module script failed/i.test(text) ||
    /error loading dynamically imported module/i.test(text)
  );
}

/**
 * Можно ли перезагрузиться сейчас. `true` — маркер поставлен, зовущий обязан
 * перезагрузить страницу; `false` — в этом окне уже пробовали.
 *
 * Отказ хранилища (приватный режим, квота) считаем «уже пробовали»: без памяти
 * о попытке перезагрузка превратилась бы в цикл — а цикл хуже экрана ошибки.
 */
export function registerStaleChunkReload(
  now: number = Date.now(),
  storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeSessionStorage(),
): boolean {
  if (!storage) return false;
  try {
    const raw = storage.getItem(MARK_KEY);
    const last = raw ? Number.parseInt(raw, 10) : Number.NaN;
    if (Number.isFinite(last) && now - last < STALE_CHUNK_RELOAD_WINDOW_MS) return false;
    storage.setItem(MARK_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

function safeSessionStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null; // приватный режим с запретом хранилища
  }
}

/**
 * Ошибка «чанка нет» → перезагрузить страницу один раз. Возвращает `true`,
 * если перезагрузка запущена (вызывающему больше делать нечего).
 */
export function reloadOnStaleChunk(error: unknown, now: number = Date.now()): boolean {
  if (!isStaleChunkError(error)) return false;
  if (!registerStaleChunkReload(now)) return false;
  try {
    // Расписка: без неё дознание сочло бы эту перезагрузку убийством вкладки
    // (reload-forensics.ts) и увело бы разбор в память вместо выкатки.
    markOurReload('протухший чанк', undefined, now);
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}
